import { useState } from 'react';
import { CheckCircle } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Loading } from '../../components/layout/Loading';
import { useEmergencyPending, useDecideEmergencyLeave } from '../../hooks';
import { useToast } from '../../components/ui/toast';

type Decision = 'paid' | 'unpaid' | 'absent';
const DECISION_LABEL: Record<Decision, string> = {
  paid: 'ลาได้เงิน',
  unpaid: 'ลาไม่ได้เงิน',
  absent: 'ขาดงาน',
};

/** Content-only version used by the Approvals hub (tabs). */
export function HREmergencyLeaveContent() {
  const { data: items, isLoading } = useEmergencyPending();
  const decide = useDecideEmergencyLeave();
  const toast = useToast();
  const [openId, setOpenId] = useState<string | null>(null);
  const [decision, setDecision] = useState<Decision>('paid');
  const [reason, setReason] = useState('');
  const [deductQuota, setDeductQuota] = useState(true);

  function startDecide(leaveId: string) {
    setOpenId(leaveId);
    setDecision('paid');
    setReason('');
    setDeductQuota(true);
  }

  async function submit(leaveId: string) {
    if (!reason.trim()) { toast.error('กรุณาระบุเหตุผล'); return; }
    try {
      await decide.mutateAsync({ leave_id: leaveId, decision, reason: reason.trim(), deduct_quota: deductQuota });
      setOpenId(null);
    } catch (e) { toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'); }
  }

  function dueLabel(due: string | null): { text: string; overdue: boolean } {
    if (!due) return { text: '', overdue: false };
    const ms = new Date(due).getTime() - Date.now();
    const days = Math.ceil(ms / 86_400_000);
    return { text: days >= 0 ? `เหลือ ${days} วัน` : `เลยกำหนด ${-days} วัน`, overdue: days < 0 };
  }

  return (
    <>
      <p className="text-sm text-gray-600 mb-4">
        ตัดสินว่าแต่ละเคสเป็น <b>ลาได้เงิน / ลาไม่ได้เงิน / ขาดงาน</b> พร้อมเหตุผล — HR รับผิดชอบการตัดสิน (มี audit). ต้องเคลียร์ให้หมดก่อนปิดงวด
      </p>

      {isLoading ? (
        <Loading />
      ) : !items || items.length === 0 ? (
        <div className="card p-6 text-center">
          <CheckCircle className="w-10 h-10 mx-auto mb-2 text-[#2f7d52]" />
          <p className="font-medium">ไม่มีลาฉุกเฉินรอตัดสิน</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((it) => {
            const due = dueLabel(it.conditional_evidence_due);
            return (
              <div key={it.leave_id} className="card p-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-medium">{it.emp_code} · {it.date}</div>
                    {it.reason && <div className="text-sm text-gray-600 mt-1">เหตุผลพนักงาน: {it.reason}</div>}
                  </div>
                  {due.text && (
                    <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${due.overdue ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800'}`}>
                      {due.text}
                    </span>
                  )}
                </div>

                {openId === it.leave_id ? (
                  <div className="mt-3 space-y-2 border-t pt-3">
                    <div className="flex gap-2">
                      {(Object.keys(DECISION_LABEL) as Decision[]).map((d) => (
                        <button
                          key={d}
                          onClick={() => setDecision(d)}
                          className={`flex-1 text-sm py-2 rounded-lg border ${decision === d ? 'bg-brand-600 text-white border-brand-600' : 'border-gray-200'}`}
                        >
                          {DECISION_LABEL[d]}
                        </button>
                      ))}
                    </div>
                    <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className="input" placeholder="เหตุผลการตัดสิน (บังคับ)" />
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={deductQuota} onChange={(e) => setDeductQuota(e.target.checked)} />
                      ตัดโควต้าวันลาด้วย
                    </label>
                    <div className="flex gap-2">
                      <button onClick={() => setOpenId(null)} className="btn-secondary flex-1 text-sm">ยกเลิก</button>
                      <button onClick={() => submit(it.leave_id)} disabled={decide.isPending} className="btn-primary flex-1 text-sm">ยืนยันการตัดสิน</button>
                    </div>
                  </div>
                ) : (
                  <button onClick={() => startDecide(it.leave_id)} className="btn-primary w-full mt-3 text-sm">ตัดสินเคสนี้</button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

/** Standalone page (kept for direct linking from legacy URLs). */
export function HREmergencyLeave() {
  return (
    <AdminLayout title="ตรวจลาฉุกเฉิน">
      <HREmergencyLeaveContent />
    </AdminLayout>
  );
}
